/**
 * Composer-side attachment staging: pick a file, upload it, hold the reference
 * until the message is sent. Only `ready` entries are sendable.
 */

import { nanoid } from '@reduxjs/toolkit';
import { useCallback, useState } from 'react';

import { uploadProjectFile } from '../api/projectFileQueries';
import type { EpiAttachment, EpiStagedAttachment } from '../types/epi-chat-types';
import { attachmentGate, attachmentRejectionReason } from '../utils/attachmentLimits';

/** Shown when a failure carries nothing the researcher can act on. */
const GENERIC_FAILURE = "Couldn't upload — remove it and try again.";

export interface UseChatAttachmentsResult {
  staged: EpiStagedAttachment[];
  /** Stage a picked file and start its upload. No-op once `atLimit`. */
  attach: (file: File) => void;
  /** Drop one staged attachment (an in-flight upload is abandoned, not cancelled). */
  remove: (id: string) => void;
  /** Drop all staged attachments — called after a successful send. */
  clear: () => void;
  /** References ready to ride a message. Excludes uploading and failed entries. */
  sendableAttachments: EpiAttachment[];
  /** True while any staged upload is still in flight. */
  hasPendingUploads: boolean;
  /** True once this message already holds its one file. */
  atLimit: boolean;
}

export const useChatAttachments = (projectId: string | undefined): UseChatAttachmentsResult => {
  const [staged, setStaged] = useState<EpiStagedAttachment[]>([]);
  const { uploading, atLimit } = attachmentGate(staged);

  const attach = useCallback(
    (file: File) => {
      if (!projectId) return;
      // Backstop for the disabled picker: a second pick never reaches the wire.
      if (atLimit) return;
      const id = nanoid();
      const base = {
        id,
        filename: file.name,
        mimeType: file.type,
        size: file.size,
      };
      // An error card, not silence — a dropped pick reads as a broken button.
      const rejection = attachmentRejectionReason(file);
      if (rejection) {
        setStaged((current) => [...current, { ...base, status: 'error', error: rejection }]);
        return;
      }
      setStaged((current) => [...current, { ...base, status: 'uploading' }]);
      void uploadProjectFile({ projectId, file })
        .then((attachment) => {
          setStaged((current) =>
            current.map((entry) =>
              entry.id === id
                ? {
                    ...entry,
                    status: 'ready',
                    fileId: attachment.fileId,
                    // The server is the authority on the stored reference.
                    filename: attachment.filename,
                    mimeType: attachment.mimeType,
                    size: attachment.size,
                  }
                : entry
            )
          );
        })
        .catch((error: unknown) => {
          // ``uploadProjectFile`` throws researcher-facing text.
          setStaged((current) =>
            current.map((entry) =>
              entry.id === id
                ? {
                    ...entry,
                    status: 'error',
                    error: error instanceof Error ? error.message : GENERIC_FAILURE,
                  }
                : entry
            )
          );
        });
    },
    [projectId, atLimit]
  );

  const remove = useCallback((id: string) => {
    setStaged((current) => current.filter((entry) => entry.id !== id));
  }, []);

  const clear = useCallback(() => setStaged([]), []);

  const sendableAttachments = staged.flatMap((entry) =>
    entry.status === 'ready' && entry.fileId
      ? [
          {
            fileId: entry.fileId,
            filename: entry.filename,
            mimeType: entry.mimeType,
            size: entry.size,
          },
        ]
      : []
  );

  return {
    staged,
    attach,
    remove,
    clear,
    sendableAttachments,
    hasPendingUploads: uploading,
    atLimit,
  };
};
